/**
 * Which market answers first for a card: eBay sold, or TCGplayer asking.
 *
 * Every card in card_prices can carry both. `stats` and `evidence` are what
 * copies actually sold for on eBay; `asks` is what TCGplayer sellers want for
 * one. The card screen has always shown the two side by side, and until now the
 * first number everywhere else - the collection tile, the scanner, the vendor
 * deal - was always the eBay one, with the ask only standing in when no sale
 * covered that condition.
 *
 * That is the right answer for vintage and the wrong one for modern. A 1999
 * Base Set Charizard trades on completed sales and the asks are fantasy; a set
 * that landed last month has barely any sold history and TCGplayer is the
 * market. So the owner draws one line through the catalogue instead:
 *
 *     this set and everything released before it  ->  eBay sold
 *     everything released after it                ->  TCGplayer
 *
 * One line, one stored set id, and this module is the only thing that reads it.
 * Nothing else may decide a source for itself, or the tile and the card screen
 * go back to disagreeing.
 *
 * The preference only chooses which source is ASKED FIRST. A card whose
 * preferred source has nothing for its condition still shows the other one, and
 * failing that the estimate off its neighbouring conditions. There is no such
 * thing here as a card with data that shows no price.
 */

import RELEASE_ORDER from '../data/set-release-order.json';

export const EBAY = 'ebay';
export const TCG = 'tcg';

/**
 * Skyridge, 12.05.2003: the last set Wizards of the Coast printed before the
 * licence went to Nintendo, and the default cut.
 *
 * Taken from digitalcards.sets rather than from memory. Skyridge is 2003-05-12
 * and Ruby & Sapphire, the first Nintendo set, is 2003-07-01, so the two eras
 * really are separated by a date and not just by a year. It is also why the
 * year alone cannot carry this rule: both sets are 2003.
 */
export const DEFAULT_CUT_SET_ID = 'ecard3';

/** What to call each source on screen. */
export const SOURCE_LABELS = Object.freeze({
  [EBAY]: 'eBay sold',
  [TCG]: 'TCGplayer',
});

/** The one sentence that explains the cut, wherever it is shown. */
export const CUT_EXPLANATION =
  'This set and everything before it: eBay sold. Everything after: TCGplayer.';

const TIMELINE = Object.freeze(
  (RELEASE_ORDER.sets || []).map(([id, name, releaseDate]) => ({ id, name, releaseDate })),
);

const key = (v) => String(v ?? '').trim().toLowerCase();

let byId = null;
let byName = null;

function indexes() {
  if (!byId) {
    byId = new Map();
    byName = new Map();
    for (const set of TIMELINE) {
      byId.set(key(set.id), set);
      // First writer wins. Two sets sharing a name would be a catalogue bug,
      // and the earlier one is the one a price row written years ago meant.
      if (!byName.has(key(set.name))) byName.set(key(set.name), set);
    }
  }
  return { byId, byName };
}

/**
 * Every set that has a release date, oldest first.
 *
 * The picker draws this list directly, so the order it shows is the order the
 * rule uses. Ten promo lines are missing on purpose: they were never released
 * on a day, so they cannot sit on a timeline, and they price by the default
 * rule. See scripts/build-catalog-index.mjs.
 */
export function setsInReleaseOrder() {
  return TIMELINE;
}

/**
 * The release date of a set, however it was named.
 *
 * A price row carries `set_name`, a catalogue row carries `id`, and a scanned
 * card carries whichever the scanner had. All three reach here, so all three
 * are tried.
 *
 * @param {string|{id?:string, set_id?:string, name?:string, set_name?:string}} set
 * @returns {string|null} "YYYY-MM-DD", or null when the set is not on the timeline
 */
export function releaseDateFor(set) {
  if (!set) return null;
  if (typeof set === 'object') {
    return releaseDateFor(set.id ?? set.set_id) ?? releaseDateFor(set.name ?? set.set_name);
  }
  const k = key(set);
  if (!k) return null;
  const { byId: ids, byName: names } = indexes();
  return (ids.get(k) ?? names.get(k))?.releaseDate ?? null;
}

/**
 * The cut the account has chosen, as a set.
 *
 * Falls back to the default whenever the stored id names a set the catalogue
 * no longer has, which is what a renamed or merged set would look like.
 */
export function cutSet(prefs) {
  const { byId: ids } = indexes();
  const stored = key(resolvePrefs(prefs)?.source_cut_set_id);
  return (stored && ids.get(stored)) || ids.get(DEFAULT_CUT_SET_ID) || null;
}

/** The stored cut set id, or the default when nothing has been chosen. */
export function cutSetId(prefs) {
  return cutSet(prefs)?.id || DEFAULT_CUT_SET_ID;
}

/**
 * Which source answers first for this card's set.
 *
 * An unknown set falls back to eBay, which is the default rule and is also what
 * this app did for every card before the preference existed. A set nobody can
 * place on the timeline must not quietly change price source.
 *
 * @param {string|object} set a set id, a set name, or a row carrying either
 * @param {object} [prefs] users.pricing_prefs; omit to use the active account's
 * @returns {'ebay'|'tcg'}
 */
export function sourceForSet(set, prefs) {
  const cut = cutSet(prefs);
  if (!cut) return EBAY;
  const released = releaseDateFor(set);
  if (!released) return EBAY;
  return released <= cut.releaseDate ? EBAY : TCG;
}

/** A prefs object with the cut set to this id, ready to store. */
export function withCutSet(prefs, setId) {
  return { ...(prefs || {}), source_cut_set_id: setId };
}

// ---- The active account's preference ----
//
// The price selection runs in cardPriceCache, several layers below any screen,
// and most of its callers have no profile in hand: the vendor deal, the bulk
// scanner and the collection all call getBulkCardPrices with a null prefs.
// Threading the profile through every one of them would have meant four places
// that could forget, and a collection tile that disagreed with the card screen
// is exactly what this feature exists to stop. So the profile publishes its
// prefs once, here, and every reader without its own copy uses these.

let active = null;

/** Called by ProfileContext whenever the active profile's prefs change. */
export function setPricingPrefs(prefs) {
  active = prefs || null;
}

/** The published prefs, for a caller that was given none. */
export function activePricingPrefs() {
  return active;
}

function resolvePrefs(prefs) {
  return prefs || active || null;
}

/** For tests. */
export function _resetPricingPrefs() {
  active = null;
}
